/**
 * Roles page - Lists all hiring roles with resume bank
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { roleAPI, Role } from '../api/client';
import TopRibbon from '../components/TopRibbon';
import RoleRow from '../components/RoleRow';
import './Roles.css';

const Roles: React.FC = () => {
  const navigate = useNavigate();

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'active' | 'paused' | 'closed' | 'draft' | 'all'>('all');
  const [deletingRole, setDeletingRole] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);

  const fetchRoles = React.useCallback(async () => {
    try {
      const filter = statusFilter === 'all' ? undefined : statusFilter;
      const response = await roleAPI.listRoles(filter);
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRoles();
  }, [statusFilter, fetchRoles]);

  // Auto-poll every 10s when any role is processing
  useEffect(() => {
    const hasProcessing = roles.some(r => r.is_processing);
    if (!hasProcessing) return;

    const interval = setInterval(() => {
      fetchRoles();
    }, 10000);

    return () => clearInterval(interval);
  }, [roles, fetchRoles]);

  const handleDeleteDraft = async (roleId: string) => {
    if (!window.confirm('Delete this draft role? This cannot be undone.')) {
      return;
    }

    setDeletingRole(roleId);
    try {
      await roleAPI.deleteRole(roleId);
      fetchRoles();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to delete draft role';
      alert(message);
    } finally {
      setDeletingRole(null);
    }
  };

  const handleRetryProcessing = async (taskId: string) => {
    setRetrying(taskId);
    try {
      const response = await roleAPI.retryFailedTask(taskId);
      const data = response.data;

      if (data?.status === 'completed') {
        alert(`All ${data.resumes_processed || 0} resumes are already processed. Try re-uploading the binder if you expect more.`);
      }

      await new Promise(resolve => setTimeout(resolve, 2000));
      await fetchRoles();
    } catch (error: any) {
      console.error('Retry failed:', error);
      const message = error.response?.data?.detail || 'Failed to retry. Please try again.';
      alert(message);
    } finally {
      setRetrying(null);
    }
  };

  return (
    <>
      <TopRibbon />
      <div className="roles-container" style={{ paddingTop: '64px' }}>
        <div className="roles-wrapper">
        {/* Filter bar with create button */}
        <div className="roles-filter" style={{ justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label>Status:</label>
            <div className="filter-buttons">
              {(['active', 'draft', 'paused', 'closed', 'all'] as const).map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={statusFilter === status ? 'filter-btn active' : 'filter-btn'}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => navigate('/roles/new')}
            style={{
              padding: '8px 16px', background: '#2d5016', color: 'white', border: 'none',
              borderRadius: '6px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.2s', whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#3d6b1f'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#2d5016'}
          >
            + Create Role
          </button>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading roles...</p>
          </div>
        ) : roles.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h2>No roles yet</h2>
            <p>
              {statusFilter === 'active'
                ? 'Create your first hiring role to start building your resume bank.'
                : `No ${statusFilter} roles found. Try a different filter.`}
            </p>
            {statusFilter === 'active' && (
              <button onClick={() => navigate('/roles/new')} className="btn-primary">
                Create Your First Role
              </button>
            )}
          </div>
        ) : (
          <div className="roles-table-wrapper" style={{ marginTop: '24px' }}>
            <table className="roles-table" style={{
              width: '100%', borderCollapse: 'collapse', background: 'white',
              borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
            }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Role</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Status</th>
                  <th style={{ padding: '16px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Candidates</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Last Evaluated</th>
                  <th style={{ padding: '16px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map(role => (
                  <RoleRow
                    key={role.id}
                    role={role}
                    retrying={retrying}
                    deletingRole={deletingRole}
                    onRetry={handleRetryProcessing}
                    onDeleteDraft={handleDeleteDraft}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default Roles;
